import React, { useEffect, useState } from 'react';
import { PageLayout } from '../components/PageLayout';
import {
  getAuditHistory,
  ApiRequestError,
  type HistoryReport,
} from '../services/api';

export function History() {
  const [reports, setReports] = useState<HistoryReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  async function fetchHistory() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAuditHistory();
      setReports(data);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An error occurred loading history');
      }
    } finally {
      setIsLoading(false);
    }
  }

  function formatDate(isoDate: string): string {
    return new Date(isoDate).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  return (
    <PageLayout>
      <main className="history-page" role="main" aria-labelledby="history-heading">
        <h2 id="history-heading">Audit History</h2>
        <p>Your retained audit reports, ordered by pay period (up to 52 reports).</p>

        {isLoading && (
          <div className="loading" aria-live="polite" role="status">
            Loading audit history...
          </div>
        )}

        {error && (
          <div className="error-message" role="alert" aria-live="assertive">
            {error}
          </div>
        )}

        {!isLoading && !error && reports.length === 0 && (
          <p className="empty-state" aria-live="polite">
            No retained audit reports found. Enable data retention in Settings to keep your reports.
          </p>
        )}

        {!isLoading && !error && reports.length > 0 && (
          <table className="history-table" aria-label="Audit history reports">
            <thead>
              <tr>
                <th scope="col">Pay Period</th>
                <th scope="col">Status</th>
                <th scope="col">Created</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.auditId}>
                  <td>
                    {formatDate(report.periodStart)} – {formatDate(report.periodEnd)}
                  </td>
                  <td>
                    <span
                      className={
                        report.hasDiscrepancies
                          ? 'status-discrepancy'
                          : 'status-ok'
                      }
                      aria-label={
                        report.hasDiscrepancies
                          ? 'Has discrepancies'
                          : 'No discrepancies'
                      }
                    >
                      {report.hasDiscrepancies ? 'Discrepancies found' : 'All correct'}
                    </span>
                  </td>
                  <td>{formatDate(report.createdAt)}</td>
                  <td>
                    <a
                      href={`/audit/${report.auditId}`}
                      className="view-report-link"
                      aria-label={`View report for ${formatDate(report.periodStart)} to ${formatDate(report.periodEnd)}`}
                    >
                      View Report
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </PageLayout>
  );
}
